import TRANSITION from '@integrations/pint-appstore-commons/src/enums/APP_STATUS_TRANSITION.json';
import { ACTION_TYPE, fetchJson } from '../../store/actionCreatorsHelpers';
import APP_SETTINGS_STATUS_ACTION from '../../enums/APP_SETTINGS_STATUS_ACTION';
import APP_SETTINGS_EDITORS_ACTION from '../../enums/APP_SETTINGS_EDITORS_ACTION';
import { AppId, SelectedGroup, CollaborationAppsPrioritizationData } from './interfaces';
import { CoeditPolicy } from '../editors/editorsReducer';
import { CustomData } from './types';

interface Status {
    transition: string;
    groups: string[];
}
interface AppSettings {
    appId: string;
    status: Status;
    editors: CollaborationAppsPrioritizationData;
}
const transitionToAction = {
    [TRANSITION.DISABLE]: APP_SETTINGS_STATUS_ACTION.DISABLE_APP,
    [TRANSITION.ALLOW]: APP_SETTINGS_STATUS_ACTION.ENABLE_APP_ALLOW,
    [TRANSITION.ADD]: APP_SETTINGS_STATUS_ACTION.ENABLE_APP_ADD,
    [TRANSITION.GROUP]: APP_SETTINGS_STATUS_ACTION.ENABLE_APP_GROUP,
};

export function addApp(appId: string, name: string, userSettings: string) {
    return {
        type: ACTION_TYPE.APP_SETTINGS_CHANGED,
        appId,
        payload: fetchJson(`api/user/apps/${appId}`, {
            method: 'PUT',
        }).then(({ status }: any) => ({
            status,
            appId,
            name,
            userSettings,
            statusAction: APP_SETTINGS_STATUS_ACTION.ADD_APP,
            editorsAction: APP_SETTINGS_EDITORS_ACTION.NO_CHANGE,
        })),
    };
}

export function removeApp(appId: string) {
    return {
        type: ACTION_TYPE.APP_SETTINGS_CHANGED,
        appId,
        payload: fetchJson(`api/user/apps/${appId}`, {
            method: 'DELETE',
        }).then((body: any) => ({
            status: body?.status,
            appId,
            statusAction: APP_SETTINGS_STATUS_ACTION.REMOVE_APP,
            editorsAction: APP_SETTINGS_EDITORS_ACTION.NO_CHANGE,
        })),
    };
}

export function updateCoeditPolicy(coeditPolicy: CoeditPolicy) {
    return {
        type: ACTION_TYPE.UPDATE_COEDIT_POLICY,
        payload: fetchJson(`api/editors/coedit-policy`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                coeditPolicy,
            }),
        }),
    };
}

export function updateCustomData(appId: string, data?: CustomData) {
    return {
        type: ACTION_TYPE.UPDATE_CUSTOM_DATA,
        payload: fetchJson(`api/tenant/apps/customdata/${appId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        }).then(({ status }: any) => status),
    };
}

export function getCoeditPolicy() {
    return {
        type: ACTION_TYPE.REQUEST_COEDIT_POLICY,
        payload: fetchJson(`api/editors/coedit-policy`),
    };
}

export function searchGroups(phrase: string, page = 1) {
    return {
        type: ACTION_TYPE.REQUEST_SEARCH_GROUPS,
        payload: fetchJson(`api/groups/search/${encodeURIComponent(phrase)}?page=${page}`),
    };
}

export function getAddedGroups(appId: string, groups: string[] = []) {
    return {
        type: ACTION_TYPE.REQUEST_GET_ADDED_GROUPS,
        payload: fetchJson(`api/added-groups/${appId}?ids=${groups.join(',')}`),
    };
}

export function openControlCenter() {
    return {
        type: ACTION_TYPE.OPEN_CONTROL_CENTER,
    };
}

// TODO possibly we would have to change a text displayd for this notification
export function notifySuggestSettingDefaultApp() {
    return {
        type: ACTION_TYPE.SUGGEST_SETTING_DEFAULT_APP,
    };
}

export function fetchStatusFrom(appId: string) {
    return {
        type: ACTION_TYPE.REQUEST_STATUS_FORM,
        payload: fetchJson(`api/apps/${appId}`, {
            method: 'GET',
        }).then(
            ({
                name,
                status,
                groups,
                domainSettings,
                domainSettingsRequired,
                domainSettingsEnabledFromFeatureFlag,
                userManual,
                configurationStatus,
                type,
                isProtectConfigurable,
                customData,
            }: any) => ({
                appId,
                name,
                status,
                groups,
                domainSettings,
                domainSettingsRequired,
                userManual,
                domainSettingsEnabledFromFeatureFlag,
                areGroupsFetching: false,
                configurationStatus,
                type,
                isProtectConfigurable,
                ...(customData && { customData }),
            })
        ),
    };
}

export function closeStatusForm() {
    return {
        type: ACTION_TYPE.CLOSE_STATUS_FORM,
    };
}

export function fetchDomainSettingsToken(appId: string) {
    return {
        type: ACTION_TYPE.REQUEST_DOMAIN_SETTINGS_TOKEN,
        payload: fetchJson(`api/domainSettingsToken/${appId}`),
    };
}

/**
 * Updates application settings including status, groups, and editor configurations
 * @param {AppSettings} settings - Object containing app settings
 * @param {string} settings.appId - Unique identifier for the application
 * @param {Status} settings.status - Contains transition type and groups for app status
 * @param {CollaborationAppsPrioritizationData} settings.editors - Editor configuration and hierarchy
 * @param {boolean} wasTheAppSetAsDefault - Indicates if app was set as default
 * @param {boolean} [isProtectConfigurable] - Determines if app uses tenant or domain-level configuration
 * @returns {Object} Redux action with payload containing updated app settings
 */
export function changeAppSettings(
    { appId, status, editors }: AppSettings,
    wasTheAppSetAsDefault: boolean,
    isProtectConfigurable?: boolean
) {
    return {
        type: ACTION_TYPE.APP_SETTINGS_CHANGED,
        appId,
        payload: getPayload(appId, status, editors, wasTheAppSetAsDefault, isProtectConfigurable),
    };
}

export function updateAppTransition(transition: string) {
    return {
        type: ACTION_TYPE.UPDATE_TRANSITION,
        payload: { transition },
    };
}

export function updateSelectedGroups(groups: SelectedGroup[]) {
    return {
        type: ACTION_TYPE.UPDATE_SELECTED_GROUPS,
        payload: { groups },
    };
}

export function updateCollaborationAppsPriorityList(hierarchy: AppId[]) {
    return {
        type: ACTION_TYPE.UPDATE_COLLABORATION_APPS_HIERARCHY,
        payload: hierarchy,
    };
}

type PayloadResult = {
    appId: string;
    statusAction: string;
    editorsAction: string;
    status: string | null;
    wasTheAppSetAsDefault?: boolean;
};

function getPayload(
    appId: string,
    status: Status,
    editors: CollaborationAppsPrioritizationData,
    wasTheAppSetAsDefault?: boolean,
    isProtectConfigurable?: boolean
) {
    const result: PayloadResult = {
        appId,
        statusAction: APP_SETTINGS_STATUS_ACTION.NO_CHANGE,
        editorsAction: APP_SETTINGS_EDITORS_ACTION.NO_CHANGE,
        status: null,
    };

    if (typeof wasTheAppSetAsDefault === 'boolean') {
        result.wasTheAppSetAsDefault = wasTheAppSetAsDefault;
        result.statusAction = APP_SETTINGS_STATUS_ACTION.CHANGE_DEFAULT;
    }

    return Promise.resolve()
        .then(() => {
            if (status) {
                if (status.transition === TRANSITION.GROUP) {
                    return changeGroups(appId, status.groups);
                }
                if (status.transition) {
                    return changeAppStatus(appId, status.transition, isProtectConfigurable);
                }
            }
        })
        .then((newStatus) => {
            if (newStatus) {
                result.status = newStatus;
            }

            if (editors) {
                return updateCollaborationAppsHierarchy({
                    collaborationAppsHierarchy: editors.collaborationAppsHierarchy,
                    collaborationAppsPrioritizationEnabled: editors.collaborationAppsPrioritizationEnabled,
                });
            }
        })
        .then(() => {
            // TODO needs update if we want to track changes in editors priority list
            // if (editors) {
            //     result.editorsAction = editors.editorsAppDefault
            //         ? APP_SETTINGS_EDITORS_ACTION.ENABLE_DEFAULT
            //         : APP_SETTINGS_EDITORS_ACTION.DISABLE_DEFAULT;
            // }
            if (status) {
                result.statusAction = transitionToAction[status.transition];
            }

            return result;
        });
}

function changeGroups(appId: string, groups: string[]) {
    return fetchJson(`api/domain/apps/groups/${appId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            groups,
        }),
    }).then(({ status }: any) => status);
}

function changeAppStatus(appId: string, transition: string, isProtectConfigurable?: boolean) {
    const appsStatusRoute = isProtectConfigurable ? 'api/tenant/apps' : 'api/domain/apps';

    return fetchJson(`${appsStatusRoute}/${appId}`, {
        method: transition !== TRANSITION.DISABLE ? 'PUT' : 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            transition,
        }),
    }).then(({ status }: any) => status);
}

export function updateCollaborationAppsHierarchy({
    collaborationAppsHierarchy,
    collaborationAppsPrioritizationEnabled,
}: CollaborationAppsPrioritizationData): Promise<CollaborationAppsPrioritizationData> {
    return fetchJson(`api/editors/appsHierarchy`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            collaborationAppsHierarchy,
            collaborationAppsPrioritizationEnabled,
        }),
    });
}
